<script lang="ts" setup>
import { computed, useCssModule, useSlots, h, type VNode } from 'vue';
import { Keys } from '../../shared/enums';
import { injectStrict } from '../../utils';
import { OB_TABS_CONTEXT } from './shared';
interface Props {
  disabled?: boolean;
  asChild?: boolean;
  value: string;
}
interface SlotScope {
  rootProps: any;
}
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  asChild: false,
});
defineSlots<{
  default?: (props: SlotScope) => VNode;
}>();
const { setValue, isValueSelected, id, variant, selectionFollowFocus } = injectStrict(
  OB_TABS_CONTEXT,
  undefined,
  '<ObTabsButton /> must be a child of <ObTabs /> component.',
);
const selected = computed(() => isValueSelected(props.value));
function onClick() {
  setValue(props.value);
}
function onKeydown(event: KeyboardEvent) {
  if (event.key === Keys.Enter) {
    setValue(props.value);
  }
}
function onFocus() {
  if (selectionFollowFocus.value) {
    setValue(props.value);
  }
}
const style = useCssModule();
const rootProps = computed<SlotScope['rootProps']>(() => ({
  id: `${id}-tab-${props.value}`,
  tabindex: selected.value ? 0 : -1,
  role: 'tab',
  ['aria-controls']: selected.value ? `${id}-panel-${props.value}` : undefined,
  ['aria-selected']: selected.value,
  ['aria-disabled']: props.disabled || undefined,
  onClick,
  onKeydown,
  onFocus,
  class: [
    variant.value === 'secondary' ? style.tabSecondary : style.tabPrimary,
    {
      [variant.value === 'secondary' ? style.tabSecondarySelected : style.tabPrimarySelected]:
        selected.value,
      [style.disabled]: props.disabled,
    },
  ],
}));
const slots = useSlots();
defineRender(() => {
  const children = slots.default?.({ rootProps: rootProps.value });
  return props.asChild
    ? children
    : h(
        'button',
        {
          type: 'button',
          disabled: props.disabled,
          ...rootProps.value,
        },
        children,
      );
});
</script>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/colors';
@use '../../styles/iconography';
@use '../../styles/typography';

.tabPrimary,
.tabSecondary {
  @include shared.reset-button();
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  font-family: typography.$font-family-primary;
  overflow: hidden;
}

.tabPrimary {
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  padding: 8px 8px;
  color: var(--ui2-content-primary);
  border-radius: 6px 6px 0 0;
  height: 32px;

  &:focus-visible {
    outline: 1px solid colors.$hyperlink;
    outline-offset: -1px;
  }

  &:hover:not(.disabled) {
    background-color: var(--ui2-state-hover-lvl-1);
  }

  :deep(svg) {
    width: 18px;
    height: 18px;
  }
}

.tabPrimarySelected {
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: var(--ui2-primitive-lavender-mist-550);
    pointer-events: none;
  }
}

.disabled {
  color: var(--ui2-content-inactive);
  cursor: not-allowed;
  pointer-events: none;
}

.tabSecondary {
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 24px;
  padding: 4px 20px;
  height: 32px;
  font-weight: 500;
  color: var(--ui2-content-primary);
  border: 1px solid colors.$surface-16;

  &:hover {
    background-color: lighten(colors.$active, 28%);
  }

  &:focus-visible {
    outline: 1px solid colors.$hyperlink;
    outline-offset: -1px;
    z-index: 10;
  }

  &:active,
  &.tabSecondarySelected:active {
    color: var(--ui2-content-primary);
    background-color: lighten(colors.$active, 15%);
  }

  & + & {
    margin-left: -1px;
  }

  &:first-child {
    border-radius: 6px 0 0 6px;
  }

  &:last-child {
    border-radius: 0 6px 6px 0;
  }
}

.tabSecondarySelected {
  color: colors.$white;
  background-color: colors.$active;

  &:hover {
    background-color: darken(colors.$active, 8%);
  }
}
</style>
